import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { App } from 'antd';
import { Clock } from 'lucide-react';
import type { RootState } from '../store';
import { addMessage, setBehaviorSummary } from '../store/interviewSlice';
import VideoControls from '../components/VideoControls';
import InterviewerAvatar from '../components/InterviewerAvatar';
import DraggableCamera from '../components/DraggableCamera';
import { useAudioRecorder } from '../hooks/useAudioRecorder';
import { useSpeechSynthesis } from '../hooks/useSpeechSynthesis';
import { useBehaviorAnalysis } from '../hooks/useBehaviorAnalysis';
import { auth } from '../services/auth';
import { shouldEndInterview } from '../services/aiService';
import { trackInterviewStart } from '../services/behaviorTracker';
import type { Message, BehaviorSummary } from '../types';

// 挂断音效URL
const END_SOUND_URL = 'https://client-bucket.mmbang.site/audios/end.mp3';

// 预加载挂断音效
const endSoundAudio = new Audio(END_SOUND_URL);
endSoundAudio.preload = 'auto';

// 将 Blob 转换为 Base64
async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;
      // 移除 data:audio/wav;base64, 前缀
      const base64Data = base64.split(',')[1];
      resolve(base64Data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export default function InterviewPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { message: antdMessage } = App.useApp();

  // 从 Redux 获取状态
  const config = useSelector((state: RootState) => state.interview.config);
  const messages = useSelector((state: RootState) => state.interview.messages);

  const [isMuted, setIsMuted] = useState(true); // 默认禁用麦克风
  const [isCameraOff, setIsCameraOff] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isInterviewerSpeaking, setIsInterviewerSpeaking] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showSpeechEnded, setShowSpeechEnded] = useState(false);
  const [isInterviewEnded, setIsInterviewEnded] = useState(false); // 面试是否结束
  const [callDuration, setCallDuration] = useState(0); // 面试时长（秒）

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const hasInitialized = useRef(false);
  const timerRef = useRef<NodeJS.Timeout>();
  const shouldEndInterviewRef = useRef(false); // 标记是否需要结束面试
  const latestMessagesRef = useRef<Message[]>([]); // 最新的消息列表，用于跳转时包含最后一轮对话

  // 使用音频录制
  const {
    startRecording,
    stopRecording,
    isMaxDurationReached,
    maxRecordingDuration,
  } = useAudioRecorder();
  const { isSpeaking, stopSpeaking, speak, speakInterview } =
    useSpeechSynthesis();

  // 行为分析：摄像头开则分析，关则停止
  const {
    currentAnalysis,
    summary: behaviorSummary,
    isModelLoaded,
    getTimelineSnapshot,
    stopAnalysis,
  } = useBehaviorAnalysis(videoRef, !isCameraOff);
  // 用 ref 保存最新摘要，供结束面试时使用
  const behaviorSummaryRef = useRef<BehaviorSummary | null>(null);
  useEffect(() => {
    behaviorSummaryRef.current = behaviorSummary;
  }, [behaviorSummary]);

  // 监听录制时长，达到最大值时自动停止
  useEffect(() => {
    if (isMaxDurationReached && isRecording) {
      handleMaxDurationReached();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMaxDurationReached]);

  // 监听面试官说话状态
  useEffect(() => {
    const handleSpeakingChange = (event: CustomEvent<boolean>) => {
      setIsInterviewerSpeaking(event.detail);
    };
    window.addEventListener(
      'interviewer-speaking',
      handleSpeakingChange as EventListener,
    );
    return () => {
      window.removeEventListener(
        'interviewer-speaking',
        handleSpeakingChange as EventListener,
      );
    };
  }, []);

  // 通话计时器
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // 格式化通话时长
  const formatDuration = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs
        .toString()
        .padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs
      .toString()
      .padStart(2, '0')}`;
  };

  // 获取用户头像
  const userAvatar =
    auth.getUser()?.avatar ||
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Felix&backgroundColor=b6e3f4';

  // 初始化：检查配置并播放开场白
  useEffect(() => {
    if (hasInitialized.current) return;

    if (!config) {
      navigate('/', { replace: true });
      return;
    }

    hasInitialized.current = true;

    // 记录面试开始行为
    if (config) {
      trackInterviewStart({
        jobType: config.jobType,
        jobLevel: config.jobLevel,
        interviewType: config.type,
        interviewerId: config.interviewer.id,
      }).catch(() => {
        // 记录失败不影响主流程
      });
    }

    // 播放开场白语音 - 保持原有流程
    if (messages.length > 0) {
      const welcomeMessage = messages[0];
      if (welcomeMessage?.role === 'interviewer') {
        const voice = config.interviewer.voice;

        speak(
          welcomeMessage.content,
          voice,
          1,
          () => {
            window.dispatchEvent(
              new CustomEvent('interviewer-speaking', { detail: true }),
            );
          },
          () => {
            window.dispatchEvent(
              new CustomEvent('interviewer-speaking', { detail: false }),
            );
          },
        );
      }
    }

    return () => {
      stopSpeaking();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 摄像头流管理：开启/关闭摄像头
  useEffect(() => {
    const video = videoRef.current;

    if (!isCameraOff) {
      // 开启
      navigator.mediaDevices
        .getUserMedia({
          video: {
            width: { ideal: 320 },
            height: { ideal: 240 },
            facingMode: 'user',
          },
          audio: false,
        })
        .then((stream) => {
          streamRef.current = stream;
          // video 元素可能此时还未挂载，用 setTimeout 等待 DOM 渲染
          const tryAttach = () => {
            if (videoRef.current) {
              videoRef.current.srcObject = stream;
            } else {
              setTimeout(tryAttach, 50);
            }
          };
          tryAttach();
        })
        .catch((err) => {
          console.error('摄像头开启失败:', err);
          setIsCameraOff(true);
        });
    } else {
      // 关闭
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (video) {
        video.srcObject = null;
      }
    }

    return () => {
      // cleanup：此 effect 重新运行前先停止旧流
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [isCameraOff]);

  // 检测面试是否结束
  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (
        lastMessage.role === 'interviewer' &&
        lastMessage.content.includes('面试到此结束，谢谢你的参与')
      ) {
        setIsInterviewEnded(true);
      }
    }
  }, [messages]);

  // 处理录制达到最大时长 - 使用整合接口
  const handleMaxDurationReached = async () => {
    antdMessage.warning('发言最长60秒，已自动结束录制');

    // 停止录制
    setIsRecording(false);
    setIsMuted(true);
    const wavBlob = await stopRecording();

    if (wavBlob) {
      // 显示"发言结束"提示
      setShowSpeechEnded(true);
      setTimeout(() => setShowSpeechEnded(false), 2000);

      try {
        // 显示思考中提示
        setIsProcessing(true);

        // 转换为 base64
        const base64Audio = await blobToBase64(wavBlob);

        // 构建面试配置
        const interviewConfig = {
          level: config.jobLevel,
          jobType: config.jobType,
          type: config.type,
          interviewer: config.interviewer,
          userInfo: config.userInfo,
          currentRound:
            messages.filter((m) => m.role === 'interviewer').length + 1,
          isPressureInterview: config.isPressureInterview,
          hasJobDescription: config.hasJobDescription,
          jobDescription: config.jobDescription,
        };

        const voice = config.interviewer.voice;

        // 使用整合接口：ASR + Chat + TTS
        await speakInterview({
          messages,
          interviewConfig,
          voice,
          audio: base64Audio,
          onStart: () => {
            // 语音开始播放时，结束思考状态
            setIsProcessing(false);
            window.dispatchEvent(
              new CustomEvent('interviewer-speaking', { detail: true }),
            );
          },
          onEnd: () => {
            window.dispatchEvent(
              new CustomEvent('interviewer-speaking', { detail: false }),
            );
            // 语音播放完毕后，如果需要结束面试则延迟跳转
            if (shouldEndInterviewRef.current) {
              shouldEndInterviewRef.current = false;
              setTimeout(() => {
                const summary =
                  getTimelineSnapshot() || behaviorSummaryRef.current;
                navigate('/loading', {
                  replace: true,
                  state: {
                    mode: 'generate-report',
                    callDuration,
                    behaviorSummary: summary,
                  },
                });
              }, 1500);
            }
          },
          onComplete: (result) => {
            // 更新消息状态
            const updatedMessages = [...messages];

            if (result.userText?.trim()) {
              const userMessage: Message = {
                role: 'user',
                content: result.userText,
                timestamp: Date.now(),
              };
              dispatch(addMessage(userMessage));
              updatedMessages.push(userMessage);
            }

            if (result.interviewerText) {
              const assistantMessage: Message = {
                role: 'interviewer',
                content: result.interviewerText,
                timestamp: Date.now(),
              };
              dispatch(addMessage(assistantMessage));
              updatedMessages.push(assistantMessage);

              // 保存最新消息到 ref，确保跳转时包含最后一轮对话
              latestMessagesRef.current = updatedMessages;

              // 检查是否应该结束面试，标记状态供 onEnd 使用
              shouldEndInterviewRef.current =
                shouldEndInterview(updatedMessages);
            }
          },
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : '语音处理失败';
        antdMessage.error(errorMessage);
        setIsProcessing(false);
      }
    }
  };

  // 切换麦克风录制 - 使用整合接口
  const handleToggleMute = async () => {
    if (isInterviewEnded) return; // 面试结束后禁用

    if (isRecording) {
      // 停止录制
      setIsRecording(false);
      setIsMuted(true);
      const wavBlob = await stopRecording();

      if (wavBlob) {
        // 显示"发言结束"提示
        setShowSpeechEnded(true);
        setTimeout(() => setShowSpeechEnded(false), 2000);

        try {
          // 显示思考中提示
          setIsProcessing(true);

          // 转换为 base64
          const base64Audio = await blobToBase64(wavBlob);

          // 构建面试配置
          const interviewConfig = {
            level: config.jobLevel,
            jobType: config.jobType,
            type: config.type,
            interviewer: config.interviewer,
            userInfo: config.userInfo,
            currentRound:
              messages.filter((m) => m.role === 'interviewer').length + 1,
            isPressureInterview: config.isPressureInterview,
            hasJobDescription: config.hasJobDescription,
            jobDescription: config.jobDescription,
          };

          const voice = config.interviewer.voice;

          // 使用整合接口：ASR + Chat + TTS
          await speakInterview({
            messages,
            interviewConfig,
            voice,
            audio: base64Audio,
            onStart: () => {
              // 语音开始播放时，结束思考状态
              setIsProcessing(false);
              window.dispatchEvent(
                new CustomEvent('interviewer-speaking', { detail: true }),
              );
            },
            onEnd: () => {
              window.dispatchEvent(
                new CustomEvent('interviewer-speaking', { detail: false }),
              );
              // 语音播放完毕后，如果需要结束面试则延迟跳转
              if (shouldEndInterviewRef.current) {
                shouldEndInterviewRef.current = false;
                setTimeout(() => {
                  const summary =
                    getTimelineSnapshot() || behaviorSummaryRef.current;
                  navigate('/loading', {
                    replace: true,
                    state: {
                      mode: 'generate-report',
                      callDuration,
                      behaviorSummary: summary,
                    },
                  });
                }, 1500);
              }
            },
            onComplete: (result) => {
              // 更新消息状态
              const updatedMessages = [...messages];

              if (result.userText?.trim()) {
                const userMessage: Message = {
                  role: 'user',
                  content: result.userText,
                  timestamp: Date.now(),
                };
                dispatch(addMessage(userMessage));
                updatedMessages.push(userMessage);
              }

              if (result.interviewerText) {
                const assistantMessage: Message = {
                  role: 'interviewer',
                  content: result.interviewerText,
                  timestamp: Date.now(),
                };
                dispatch(addMessage(assistantMessage));
                updatedMessages.push(assistantMessage);

                // 保存最新消息到 ref，确保报告包含最后一轮对话
                latestMessagesRef.current = updatedMessages;

                // 检查是否应该结束面试，标记状态供 onEnd 使用
                shouldEndInterviewRef.current =
                  shouldEndInterview(updatedMessages);
              }
            },
          });
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : '语音处理失败';
          antdMessage.error(errorMessage);
          setIsProcessing(false);
        }
      }
    } else {
      // 开始录制前，先停止面试官语音播放
      if (isSpeaking || isInterviewerSpeaking) {
        stopSpeaking();
        window.dispatchEvent(
          new CustomEvent('interviewer-speaking', { detail: false }),
        );
      }

      // 开始录制
      setIsMuted(false);
      setIsRecording(true);
      await startRecording();
    }
  };

  const handleToggleCamera = () => {
    setIsCameraOff((prev) => !prev);
  };

  // 结束面试
  const handleEndInterview = async () => {
    if (!config || isInterviewEnded || isProcessing) return;

    // 立即停止行为分析（不关摄像头）
    stopAnalysis();

    setIsInterviewEnded(true);

    // 如果面试官正在说话，停止说话状态
    if (isSpeaking || isInterviewerSpeaking) {
      stopSpeaking();
      window.dispatchEvent(
        new CustomEvent('interviewer-speaking', { detail: false }),
      );
    }

    // 播放结束音频
    try {
      endSoundAudio.currentTime = 0;
      endSoundAudio.play().catch(() => {
        // 音频播放失败不影响主流程
      });
    } catch {
      // 音频播放失败不影响主流程
    }

    // 延迟1.5秒后跳转到报告页
    setTimeout(() => {
      const summary = getTimelineSnapshot() || behaviorSummaryRef.current;
      if (summary) {
        dispatch(setBehaviorSummary(summary));
      }
      navigate('/loading', {
        replace: true,
        state: {
          mode: 'generate-report',
          callDuration,
          behaviorSummary: summary,
        },
      });
    }, 1500);
  };

  if (!config) {
    return null;
  }

  return (
    <div className="h-screen bg-[#0F1419] flex overflow-hidden">
      {/* 左侧视频区域 */}
      <div className="flex-1 flex flex-col relative">
        {/* 顶部状态栏 - 面试时长 */}
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20">
          <div className="flex items-center gap-2 px-4 py-2">
            <Clock size={16} className="text-amber-400" />
            <span className="text-white text-sm font-mono">
              {formatDuration(callDuration)}
            </span>
          </div>
        </div>

        {/* 面试官头像上方提示区域 */}
        <div className="absolute top-16 left-1/2 transform -translate-x-1/2 z-10 flex flex-col items-center gap-2">
          {/* 正在发言提示 */}
          {isRecording && (
            <div
              className="bg-[#1E293B]/95 backdrop-blur-sm px-4 py-2.5 rounded-xl flex items-center gap-3
              border border-[#334155]/50 shadow-lg shadow-black/20"
            >
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              <span className="text-[#E2E8F0] text-sm font-medium">
                正在发言...
              </span>
            </div>
          )}

          {/* 发言结束提示 */}
          {showSpeechEnded && (
            <div
              className="bg-[#1E293B]/95 backdrop-blur-sm px-4 py-2.5 rounded-xl flex items-center gap-3
              border border-[#334155]/50 shadow-lg shadow-black/20"
            >
              <div className="w-2 h-2 bg-green-500 rounded-full" />
              <span className="text-[#E2E8F0] text-sm font-medium">
                发言结束
              </span>
            </div>
          )}
        </div>

        {/* 面试官头像 */}
        <div className="flex-1 flex items-center justify-center p-8">
          <InterviewerAvatar
            interviewer={config.interviewer}
            isSpeaking={isSpeaking || isInterviewerSpeaking}
          />
        </div>

        {/* 用户摄像头 - 可拖拽 */}
        <DraggableCamera
          videoRef={videoRef}
          isCameraOff={isCameraOff}
          analysis={currentAnalysis}
        />

        {/* 底部控制栏 */}
        <VideoControls
          isMuted={isMuted}
          isCameraOff={isCameraOff}
          onToggleMute={handleToggleMute}
          onToggleCamera={handleToggleCamera}
          onEndInterview={handleEndInterview}
          isInterviewEnded={isInterviewEnded}
          isRecording={isRecording}
          isProcessing={isProcessing}
        />
      </div>
    </div>
  );
}
